import Button from '@/shared/components/rebranded/Button/Button';
import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState
} from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { isProduction } from '../environment';

interface LayoutContextProps {
  isRebrand: boolean;
  setIsRebrand: (isRebrand: boolean) => void;
}

const LayoutContext = createContext<LayoutContextProps | undefined>(undefined);

export const LayoutProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [isRebrand, setIsRebrand] = useState(false);
  // Dev toggle to force rebrand off
  const [rebrandForcedOff, setRebrandForcedOff] = useState(false);
  const rebrandValue = isRebrand && !rebrandForcedOff;
  const memoizedValue = useMemo(
    () => ({ isRebrand: rebrandValue, setIsRebrand }),
    [rebrandValue]
  );

  const styleToUse =
    isRebrand && !rebrandForcedOff
      ? window.env.ASSETS_REBRANDED
      : window.env.ASSETS;

  return (
    <LayoutContext.Provider value={memoizedValue}>
      <Helmet>
        <link rel="stylesheet" href={styleToUse} />
      </Helmet>
      <RebrandToggle>
        {!isProduction && (
          <Button
            onClick={() => setRebrandForcedOff(!rebrandForcedOff)}
            variant="outlined"
          >
            {rebrandForcedOff ? 'Enable' : 'Disable'} Rebrand
          </Button>
        )}
      </RebrandToggle>

      {children}
    </LayoutContext.Provider>
  );
};

const RebrandToggle = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
`;

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};
